import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '../constants';
import {
  GetSelectOptionByIdFunction,
  SearchOptions,
  SelectOptionSearchFunction,
  TypesenseCollection,
} from '../types';
import { searchCollection, TypesenseDocument } from '../utils';
import { PaymentMethodOption } from './paymentMethods.types';

const BASE_FILTER_ACTIVE = 'IsActive:true';

export const getSearchPaymentMethodSelectOptions: SelectOptionSearchFunction<
  PaymentMethodOption
> = async (
  searchValue: string = '*',
  apiKey: string,
  pageIndex: number = 1,
  options: SearchOptions = {},
) => {
  const results = await searchCollection(
    TypesenseCollection.PAYMENT_METHODS.name,
    'Name',
    searchValue,
    apiKey,
    pageIndex,
    TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
    options.filterBy
      ? `${options.filterBy} && ${BASE_FILTER_ACTIVE}`
      : BASE_FILTER_ACTIVE,
    options.sortBy ? `${options.sortBy}` : 'Name:asc',
  );
  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch paymentMethods from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.Name,
    id: item.SourceId.toString(),
    isReimbursable: item.IsPaidByEmployee,
  }));
  return mappedResults ?? [];
};

/**
 * Retrieves a paymentMethod select option by ID from Typesense.
 * @throws {Error} When the search fails, returns an error, or no paymentMethod is found with the given ID
 */
export const getPaymentMethodSelectOptionById: GetSelectOptionByIdFunction<
  PaymentMethodOption
> = async (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => {
  const results = await searchCollection(
    TypesenseCollection.PAYMENT_METHODS.name,
    'Name',
    '*',
    apiKey,
    1,
    1,
    additionalFilterOptions
      ? `${additionalFilterOptions} && SourceId:=${id}`
      : `SourceId:=${id}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch paymentMethod by id from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.Name,
    id: item.SourceId.toString(),
    isReimbursable: item.IsPaidByEmployee,
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`PaymentMethod with id ${id} not found`);
    throw new Error(`PaymentMethod with id ${id} not found`);
  }

  return mappedResults[0];
};
